import React from 'react';
import { Block } from '../../corelogic/model/blocks';
import { orderSections } from '../../corelogic/model/section';
import { BuiltInPage, orderableSlots } from '../../configuration/pageSlots';
import { BlockRenderer } from './blocks/BlockRenderer';

/**
 * Renders the sections of a built-in page in the order the editing interface
 * was told to use, with the editor's own added sections slotted in between.
 *
 * The page still builds its sections itself - they are hand-written layout,
 * not data - and hands them over keyed by name. This decides only the order,
 * so that "move Vision above Ziele" is a checkbox-level change rather than a
 * deployment.
 *
 * The page header is not part of this. It carries the h1 and stays where it
 * is; the page renders it above this component.
 */
interface PageSectionsProps {
  page: BuiltInPage;
  /** This page's sections, keyed by the slot name they belong to. */
  sections: Record<string, React.ReactNode>;
  /** The order chosen in the editing interface. */
  order?: readonly string[];
  /** The sections an editor added, by the slot they are placed before. */
  addedBefore: (slot: string) => Block[];
}

export const PageSections: React.FC<PageSectionsProps> = ({
  page,
  sections,
  order,
  addedBefore,
}) => {
  const declared = orderableSlots(page);

  // A section that is declared but not handed over renders nothing, and a
  // section handed over under a name nobody declared never renders at all.
  // Both are silent in production and both would reach an editor as "my text
  // is gone", so they are called out here where the two lists meet.
  if (process.env.NODE_ENV !== 'production') {
    const given = Object.keys(sections);
    const missing = declared.filter((key) => !given.includes(key));
    const unknown = given.filter((key) => !declared.includes(key));

    if (missing.length > 0 || unknown.length > 0) {
      // eslint-disable-next-line no-console
      console.warn(
        `[sections] "${page}" does not match src/configuration/pageSlots.json.` +
          (missing.length > 0 ? ` Declared but not rendered: ${missing.join(', ')}.` : '') +
          (unknown.length > 0 ? ` Rendered but not declared: ${unknown.join(', ')}.` : '')
      );
    }
  }

  return (
    <>
      {orderSections(declared, order).map((key) => (
        <React.Fragment key={key}>
          <BlockRenderer blocks={addedBefore(key)} />
          {sections[key]}
        </React.Fragment>
      ))}
      <BlockRenderer blocks={addedBefore('end')} />
    </>
  );
};
